import { useEffect, useState } from 'react';
import { DoorCard } from '../components/dashboard/DoorCard';
import { CountingDoorCard } from '../components/dashboard/CountingDoorCard';
import { PreemptDoorIcon } from '../components/dashboard/PreemptDoorIcon';
import { SuitSymbol } from '../components/common/SuitSymbol';
import { problemsApi } from '../api/problemsApi';
import { useAuth } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';

/** "Good morning/afternoon/evening" — edge case: must be correct at any hour. */
export function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * The dashboard (feature 023, US1) — the five entry "doors" from front.html.
 * The reference design's "My Progress" button is deliberately omitted (FR-004).
 */
export function DashboardPage() {
  usePageTitle('Dashboard — Hawkes Bridge');
  const { user } = useAuth();

  // FR-007: live count of problems waiting in the review set; the badge is
  // hidden entirely at 0 so an empty set shows no stale "waiting" pill.
  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    problemsApi
      .getReviewCount()
      .then((count) => {
        if (!cancelled) setReviewCount(count);
      })
      .catch(() => {
        /* a failed count must never block the dashboard — badge stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const greeting = greetingFor(new Date().getHours());
  const name = user?.firstName?.trim();

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-10">
      <div className="text-center">
        <h1 className="font-serif text-3xl text-ink sm:text-4xl">
          {greeting}
          {name ? `, ${name}` : ''}.
        </h1>
        <div className="mt-2 flex justify-center gap-2 text-xl tracking-widest" aria-hidden="true">
          <SuitSymbol suit="S" />
          <SuitSymbol suit="H" />
          <SuitSymbol suit="D" />
          <SuitSymbol suit="C" />
        </div>
        <p className="mt-2.5 text-ink-soft">Where would you like to begin?</p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
        <DoorCard
          to="/inventory/precision"
          variant="felt"
          icon="&#127183;"
          title="Study &middot; Practice &middot; Master"
          description="The complete Precision system — every opening bid, module by module. Read the notes, then practice at the table as long as you like."
          cta="Open the inventory"
        />
        <DoorCard
          to="/problems"
          icon="&#9889;"
          title="Just Practice — Pick Your Level"
          description="Skip the course and deal the cards. Choose your level and practice hands in that difficulty range — built for experienced players who want to get right to it."
          cta="Deal me in"
          levels
        />
        <DoorCard
          to="/inventory/competitive"
          icon="&#9876;&#65039;"
          title="Study Competitive Bidding"
          description="When the opponents interfere — overcalls, doubles, and the fights for the contract. Same path: study, practice, master."
          cta="Open competitive bidding"
        />
        {/* Feature 033: the card opens the Overcalls inventory (Jeff's problems first, then his
            real overcall decisions by their opening) rather than the problems page directly. */}
        <DoorCard
          to="/inventory/competitive/overcalls"
          icon="&#128737;&#65039;"
          title="Competitive Bidding: The overcalling side"
          description="Overcalls, takeout doubles and the follow-ups."
          cta="Practice overcalling"
        />
        {/* Feature 033 (client, 2026-10-16): Preempts left both inventories for a card of its own.
            Its page shows the Non-competitive (precision) and Competitive rows together at either
            family's address; the card uses the precision one. */}
        <DoorCard
          to="/inventory/precision/preempts"
          icon={<PreemptDoorIcon />}
          title="Preempts"
          description="Jeff's preemptive openings, his responses to partner's preempt, and his preemptive jump overcalls — with and without competition."
          cta="Practice preempts"
        />
        <DoorCard
          to="/problems?mode=important-sequences"
          variant="brass"
          icon="&#11088;"
          title="Important Precision Sequences"
          description="The auctions worth re-drilling before you play — curated by Jeff. The night-before-the-tournament list."
          cta="Drill the sequences"
        />
        <DoorCard
          to="/problems?mode=review"
          variant="brass"
          icon="&#128204;"
          title="My Review Problems"
          description="Every hand you've flagged at the table lands here — your personal problem set, growing as you play."
          cta="Run my review set"
          count={reviewCount}
        />
        {/* Counting moved off the Practice hub and stands on its own. It has to start a
            session before it can navigate, so the card carries that logic itself. */}
        <CountingDoorCard />
      </div>

      <p className="mt-8 text-center text-xs text-ink-soft">
        More rooms coming — Play of the Hand &middot; Game &amp; Slam Bidding
      </p>
    </div>
  );
}
